import React, { useState } from "react";
import {
  Box, Card, CardContent, TextField, Button, Typography,
  Stack, Alert, InputAdornment, IconButton,
} from "@mui/material";
import { Lock, Visibility, VisibilityOff, CheckCircle } from "@mui/icons-material";
import { alpha, useTheme } from "@mui/material/styles";
import { useSearchParams, useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_CLIENT_API_URL;

/**
 * Usata sia per il setup iniziale (/imposta-password?token=...)
 * sia per il reset (/reset-password?token=...)
 */
export default function SetPassword_View() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError("");

    if (!token) { setError("Link non valido. Richiedi un nuovo link."); return; }
    if (password.length < 8) { setError("La password deve essere di almeno 8 caratteri"); return; }
    if (password !== confirm) { setError("Le password non coincidono"); return; }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/client/set-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(true);
      } else {
        setError(data.error || "Errore nell'impostazione della password");
      }
    } catch {
      setError("Impossibile connettersi al server. Riprova più tardi.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
        p: 2,
      }}
    >
      <Card sx={{ width: "100%", maxWidth: 420, borderRadius: 4, boxShadow: "0 24px 64px rgba(0,0,0,0.2)" }}>
        <CardContent sx={{ p: 4 }}>
          {success ? (
            <Stack alignItems="center" spacing={2} textAlign="center">
              <CheckCircle sx={{ fontSize: 64, color: "success.main" }} />
              <Typography variant="h6" fontWeight={700}>Password impostata!</Typography>
              <Typography variant="body2" color="text.secondary">
                Ora puoi accedere al portale con le tue credenziali.
              </Typography>
              <Button variant="contained" fullWidth onClick={() => navigate("/login")}>
                Vai al login
              </Button>
            </Stack>
          ) : (
            <>
              <Stack alignItems="center" spacing={1} mb={3}>
                <Lock sx={{ fontSize: 40, color: "primary.main" }} />
                <Typography variant="h6" fontWeight={700}>Imposta la tua password</Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Scegli una password sicura di almeno 8 caratteri.
                </Typography>
              </Stack>

              <form onSubmit={handleSubmit}>
                <Stack spacing={2.5}>
                  {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}
                  {!token && (
                    <Alert severity="warning">
                      Link mancante o non valido. Richiedi un nuovo link al tuo referente Ecotrade.
                    </Alert>
                  )}

                  <TextField
                    label="Nuova password"
                    type={showPwd ? "text" : "password"}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(""); }}
                    fullWidth
                    disabled={!token}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Lock sx={{ color: "text.secondary" }} /></InputAdornment>,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPwd((s) => !s)} edge="end">
                            {showPwd ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    label="Conferma password"
                    type={showPwd ? "text" : "password"}
                    value={confirm}
                    onChange={(e) => { setConfirm(e.target.value); setError(""); }}
                    fullWidth
                    disabled={!token}
                    error={confirm.length > 0 && confirm !== password}
                    helperText={confirm.length > 0 && confirm !== password ? "Le password non coincidono" : ""}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Lock sx={{ color: "text.secondary" }} /></InputAdornment>,
                    }}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    size="large"
                    disabled={loading || !token}
                    sx={{ py: 1.5, borderRadius: 2, fontWeight: 600 }}
                  >
                    {loading ? "Salvataggio..." : "Imposta password"}
                  </Button>
                </Stack>
              </form>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
